<template>
  <div class="c-bitts-dropdown-menu">
    <BittsPopover
      placement="bottomLeft"
      :open="!disabled && isOpen"
      @update:open="(val) => !disabled && (isOpen = !!val)"
      :destroy-on-close="true"
      :auto-adjust-overflow="false"
      :mount-to-body="true"
      overlay-class="c-bitts-dropdown-menu__popover"
      class="h-full"
    >
      <div class="c-bitts-dropdown-menu__trigger">
        <slot :options="options" :is-open="isOpen" />
      </div>

      <template v-if="!disabled" #content>
        <div class="c-bitts-dropdown-menu__content">
          <div v-if="menuStack.length > 1" class="breadcrumb-wrapper truncate">
            <BittsButton
              variant="ghost"
              size="x-small"
              type="neutral"
              :left-icon="['fak', 'chevron-left']"
              :text="currentLayer?.label"
              @click="popStack"
            />
          </div>

          <BittsOptionList
            v-model="selected"
            :search-term="searchTerm"
            :options="normalizedOptions"
            :mode="mode"
            :show-checkbox="showCheckbox"
            :variant="variant"
            :use-search="useSearch"
            :search-placeholder="searchPlaceholder"
            :active-parent-values="activeParentValues"
            @update:search-term="searchTerm = $event"
            @change="handleChange"
            @item-click="handleOptionClick"
          >
            <template v-if="$slots['search-top']" #search-top>
              <slot name="search-top" />
            </template>

            <template v-if="$slots.prefix" #prefix="{ option }">
              <slot name="prefix" :option="option" />
            </template>

            <template #suffix="{ option }">
              <slot v-if="$slots.suffix" name="suffix" :option="option" />

              <template v-if="hasChildren(option)">
                <BittsCount
                  type="neutral"
                  :count="countLeaves(option.children)"
                  class="h-18"
                />
                <div class="text-neutral-text-weak">
                  <FontAwesomeIcon
                    :icon="['fak', 'chevron-right']"
                    class="w-3 h-3"
                  />
                </div>
              </template>
            </template>

            <template v-if="$slots.option" #default="{ option }">
              <slot name="option" :option="option" />
            </template>
          </BittsOptionList>

          <footer
            v-if="$slots.footer"
            class="flex flex-col gap-2 border-t p-6 border-neutral-border"
          >
            <slot name="footer" />
          </footer>
        </div>
      </template>
    </BittsPopover>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="
    TOption extends { label: string; value: any },
    TInput extends
      | TOption
      | {
          label: string;
          options: readonly any[];
          useBorder?: boolean;
          initialOptionsExist?: boolean;
        }
  "
>
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, onBeforeUnmount, shallowRef, watch } from 'vue';

import BittsButton from '../BittsButton/BittsButton.vue';
import BittsCount from '../BittsCount/BittsCount.vue';
import BittsOptionList, {
  type ListItem,
} from '../BittsOptionList/BittsOptionList.vue';
import BittsPopover from '../BittsPopover/BittsPopover.vue';
import { useGroupedMenuItems } from '../composables/useGroupedMenuItems';

import type { MenuLeaf, MenuNode } from './types';

// --- Types ---
// Extract leaf options from input - one level deep only (group.options or direct leaf)
type ExtractLeaf<T> = T extends { options: readonly (infer U)[] }
  ? U extends { label: string; value: any }
    ? U
    : never
  : T extends { label: string; value: any }
    ? T
    : never;

// Merge union members into single type with optional properties for non-common keys
type AllKeys<U> = U extends unknown ? keyof U : never;
type CommonKeys<U> = keyof (U extends unknown ? U : never);
type AllValues<U, K extends PropertyKey> = U extends unknown
  ? K extends keyof U
    ? U[K]
    : never
  : never;
type MergedUnion<U> = {
  [K in CommonKeys<U>]: AllValues<U, K>;
} & {
  [K in Exclude<AllKeys<U>, CommonKeys<U>>]?: AllValues<U, K>;
};

// Widen literal types to base types (e.g., 'foo' -> string)
type Widen<T> = T extends string
  ? string
  : T extends number
    ? number
    : T extends boolean
      ? boolean
      : T;

type ExtractedOption = ExtractLeaf<TInput>;
type InferredOption = MergedUnion<ExtractedOption>;
type InferredValue = Widen<ExtractedOption['value']>;

// Internal aliases using the component's inferred option type
type InternalMenuLeaf = MenuLeaf<InferredOption>;
type InternalMenuNode = MenuNode<InferredOption>;

type StackLayer = {
  label: string;
  options: readonly InternalMenuNode[];
};

const props = withDefaults(
  defineProps<{
    options: readonly TInput[];
    useSearch?: boolean;
    variant?: 'upsell' | 'default';
    searchPlaceholder?: string;
    mode?: 'single' | 'multi';
    showCheckbox?: boolean;
    disabled?: boolean;
    externalActiveParents?: Set<string | number>;
  }>(),
  {
    useSearch: true,
    variant: 'default',
    searchPlaceholder: 'Search',
    mode: 'multi',
    showCheckbox: false,
    disabled: false,
    externalActiveParents: undefined,
  },
);

const emit = defineEmits<{
  'selected:options': [options: InferredOption[]];
  'item-click': [option: InferredOption];
}>();

defineSlots<{
  default: (props: { options: readonly TInput[]; isOpen: boolean }) => any;
  prefix: (props: { option: InferredOption }) => any;
  suffix: (props: { option: InferredOption }) => any;
  option: (props: { option: InferredOption }) => any;
  'search-top': () => any;
  'end-of-list': () => any;
  footer: () => any;
}>();

// --- Models ---
const selected = defineModel<
  InferredValue[] | InferredValue | undefined | null
>();
const isOpen = defineModel<boolean>('isOpen', { default: false });
const searchTerm = defineModel<string>('searchTerm', { default: '' });

// --- Helpers ---
const { isMenuGroup, flattenMenuLeaves } = useGroupedMenuItems();

const hasChildren = (item: InternalMenuLeaf) => !!item.children?.length;

function countLeaves(nodes?: readonly InternalMenuNode[]): number {
  if (!nodes) return 0;
  return nodes.reduce(
    (count, item) =>
      count + (isMenuGroup(item) ? countLeaves(item.options) : 1),
    0,
  );
}

// Flatten tree to find selected objects later
const allLeafOptions = computed(() =>
  flattenMenuLeaves(props.options as readonly InternalMenuNode[]),
);

// Build child->parent map once when options change
const parentMap = computed(() => {
  const map = new Map<InferredValue, InferredValue>();

  function traverse(
    nodes: readonly InternalMenuNode[],
    parentVal?: InferredValue,
  ) {
    for (const node of nodes) {
      if (isMenuGroup(node)) {
        // Groups are transparent containers; pass the existing parentVal through
        traverse(node.options, parentVal);
      } else {
        // Map this node to its parent (if one exists)
        if (parentVal !== undefined) {
          map.set(node.value, parentVal);
        }
        // Recurse if children exist, setting this node as the new parent
        if (node.children) {
          traverse(node.children, node.value);
        }
      }
    }
  }

  traverse(props.options as readonly InternalMenuNode[]);
  return map;
});

const activeParentValues = computed(() => {
  const parents = new Set<InferredValue>();

  // Add external active parents if provided
  if (props.externalActiveParents) {
    for (const val of props.externalActiveParents) {
      parents.add(val as InferredValue);
    }
  }

  const raw = selected.value;
  const selectedItems = Array.isArray(raw) ? raw : raw != null ? [raw] : [];

  // For every selected item, walk up the tree using the Map
  for (const val of selectedItems) {
    let current = val;
    let parent = parentMap.value.get(current);
    const visited = new Set<InferredValue>([current]);
    while (parent !== undefined) {
      if (visited.has(parent)) break;
      parents.add(parent);
      visited.add(parent);
      current = parent;
      parent = parentMap.value.get(current);
    }
  }

  return parents;
});

// --- Navigation Stack ---
const menuStack = shallowRef<StackLayer[]>([
  { label: 'Menu', options: props.options as readonly MenuNode[] },
]);

const currentLayer = computed(() => menuStack.value.at(-1));
const currentOptions = computed(() => currentLayer.value?.options ?? []);

// Reset stack when options change
watch(
  () => props.options,
  (newOptions) => {
    menuStack.value = [
      { label: 'Menu', options: newOptions as readonly MenuNode[] },
    ];
  },
);

// Reset stack on close (with delay for animation)
let closeTimer: ReturnType<typeof setTimeout> | null = null;

watch(isOpen, (val) => {
  if (val && closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  } else if (!val) {
    closeTimer = setTimeout(() => {
      menuStack.value = [
        { label: 'Menu', options: props.options as readonly MenuNode[] },
      ];
      searchTerm.value = '';
    }, 200);
  }
});

onBeforeUnmount(() => {
  if (closeTimer) clearTimeout(closeTimer);
});

function pushStack(item: MenuLeaf) {
  if (!item.children) return;

  menuStack.value = [
    ...menuStack.value,
    { label: item.label, options: item.children },
  ];

  searchTerm.value = '';
}

function popStack() {
  if (menuStack.value.length > 1) {
    menuStack.value = menuStack.value.slice(0, -1);
  }
}

const normalizedOptions = computed<ListItem<InternalMenuLeaf>[]>(() => {
  return currentOptions.value.flatMap((item, index) => {
    // Groups - convert to flat structure with headers/dividers
    if (isMenuGroup(item)) {
      const leaves = item.options.filter(
        (opt) => !isMenuGroup(opt),
      ) as InternalMenuLeaf[];

      if (!leaves.length) return [];

      const groupHeader: ListItem<InternalMenuLeaf>[] = [];
      if (item.useBorder && index > 0) groupHeader.push({ type: 'divider' });
      if (item.label) {
        groupHeader.push({
          type: 'group',
          label: item.label,
          useBorder: item.useBorder,
        });
      }

      return [...groupHeader, ...leaves];
    }

    // Leaves
    return [item];
  });
});

function handleOptionClick(item: InternalMenuLeaf, event: MouseEvent) {
  if (hasChildren(item)) {
    event.preventDefault();
    pushStack(item);
  } else {
    emit('item-click', item);
    if (props.mode === 'single') {
      isOpen.value = false;
    }
  }
}

function handleChange(values: unknown) {
  if (values === undefined) return;

  const valuesArray = Array.isArray(values)
    ? values
    : [values as InferredValue];
  const selectedOpts = valuesArray
    .map((val) => allLeafOptions.value.find((opt) => opt.value === val))
    .filter((opt): opt is InternalMenuLeaf => !!opt);

  emit('selected:options', selectedOpts);
}
</script>

<style scoped lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";

.c-bitts-dropdown-menu {
  @apply h-full;
}

.c-bitts-dropdown-menu__trigger {
  @apply h-full;
}

.breadcrumb-wrapper {
  @apply flex items-center gap-2 p-6 pb-0 min-w-0 overflow-hidden;

  :deep(.c-bitts-btn__text) {
    @apply block overflow-hidden text-ellipsis;
  }

  :deep(.ant-btn) {
    @apply min-w-0 max-w-full overflow-hidden;
  }

  :deep(.ant-btn) > div {
    @apply min-w-0 overflow-hidden;
  }
}
</style>

<style lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";

.c-bitts-dropdown-menu__popover.ant-popover {
  .ant-popover-inner {
    @apply min-w-[100px] max-w-[320px];
  }
}
</style>
